import { Component } from '@theme/component';

const STORAGE_KEY = 'a11yPrefs';
const PREFS_VERSION = 1;

/** @typedef {Record<string, string | number | boolean>} A11yPrefs */

/** @type {Readonly<A11yPrefs>} */
const DEFAULT_PREFS = Object.freeze({
  v: PREFS_VERSION,
  contrast: 'off',
  text: 100,
  lines: 'normal',
  links: false,
  headings: false,
  font: false,
  cursor: false,
  motion: false,
  focus: false,
});

/** @type {Record<string, ReadonlyArray<string | number>>} */
const CYCLES = {
  contrast: ['off', 'high', 'invert', 'mono'],
  text: [100, 115, 130, 150],
  lines: ['normal', '16', '20'],
};

/**
 * Accessibility preferences widget (IS 5568 / תקנה 35 comfort tool).
 *
 * Toggles `a11y-*` classes on <html> only — never mutates content DOM.
 * The class map lives in the inline bootstrap script in layout/theme.liquid
 * (`window.a11yApplyPrefs`), which also re-applies persisted preferences
 * before first paint to avoid a flash of default styling.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} trigger
 * @property {HTMLElement} panel
 * @property {HTMLElement} announcer
 * @property {HTMLButtonElement[]} controls
 *
 * @extends {Component<Refs>}
 */
class AccessibilityWidget extends Component {
  requiredRefs = ['trigger', 'panel', 'announcer'];

  /** @type {A11yPrefs} */
  #prefs = this.#readPrefs();

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('keydown', this.#onKeydown);
    document.addEventListener('click', this.#onDocumentClick);
    // Safety net in case the head bootstrap did not run (e.g. blocked storage).
    this.#applyPrefs();
    this.#syncControls();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this.#onKeydown);
    document.removeEventListener('click', this.#onDocumentClick);
  }

  get #isOpen() {
    return this.refs.panel instanceof HTMLElement && !this.refs.panel.hidden;
  }

  togglePanel = () => {
    this.#isOpen ? this.closePanel() : this.openPanel();
  };

  openPanel() {
    const { panel, trigger } = this.refs;
    if (!(panel instanceof HTMLElement)) return;
    panel.hidden = false;
    trigger.setAttribute('aria-expanded', 'true');
    const first = panel.querySelector('button, a');
    if (first instanceof HTMLElement) first.focus();
  }

  /** @param {{ restoreFocus?: boolean } | Event} [options] */
  closePanel(options = {}) {
    const { panel, trigger } = this.refs;
    if (!this.#isOpen || !(panel instanceof HTMLElement)) return;
    panel.hidden = true;
    trigger.setAttribute('aria-expanded', 'false');
    if (!('restoreFocus' in options) || options.restoreFocus !== false) {
      if (trigger instanceof HTMLElement) trigger.focus();
    }
  }

  /**
   * Binary preferences — `on:click="/toggle?key=links"`.
   * @param {{ key?: string }} data
   */
  toggle = (data) => {
    const key = data?.key;
    if (!key || typeof DEFAULT_PREFS[key] !== 'boolean') return;
    this.#setPrefs(key, !this.#prefs[key]);
  };

  /**
   * Cycling preferences — `on:click="/cycle?key=contrast"`.
   * @param {{ key?: string }} data
   */
  cycle = (data) => {
    const key = data?.key;
    const cycle = key ? CYCLES[key] : undefined;
    if (!cycle || !key) return;
    const current = /** @type {string | number} */ (this.#prefs[key]);
    const next = cycle[(cycle.indexOf(current) + 1) % cycle.length];
    if (next !== undefined) this.#setPrefs(key, next);
  };

  reset = () => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // storage unavailable — in-memory state still resets
    }
    this.#prefs = { ...DEFAULT_PREFS };
    this.#applyPrefs();
    this.#syncControls();
    this.#announce(this.dataset.resetMessage ?? '');
  };

  /**
   * @param {string} key
   * @param {string | number | boolean} value
   */
  #setPrefs(key, value) {
    this.#prefs = { ...this.#prefs, [key]: value, v: PREFS_VERSION };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.#prefs));
    } catch {
      // storage unavailable — preference applies for this page only
    }
    this.#applyPrefs();
    this.#syncControls();
    this.#announceChange(key);
  }

  #applyPrefs() {
    const apply = /** @type {{ a11yApplyPrefs?: (prefs: A11yPrefs) => void }} */ (window).a11yApplyPrefs;
    if (typeof apply === 'function') apply(this.#prefs);
  }

  #readPrefs() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.v === PREFS_VERSION) return { ...DEFAULT_PREFS, ...parsed };
      }
    } catch {
      // corrupted or unavailable storage — fall back to defaults
    }
    return { ...DEFAULT_PREFS };
  }

  #controls() {
    const { controls } = this.refs;
    return Array.isArray(controls) ? controls : [];
  }

  #syncControls() {
    for (const button of this.#controls()) {
      const key = button.getAttribute('data-key');
      if (!key || !(key in DEFAULT_PREFS)) continue;
      const value = this.#prefs[key];
      const label = button.getAttribute('data-label') ?? '';

      if (button.hasAttribute('data-cycle')) {
        const valueText = button.getAttribute(`data-value-${value}`) ?? '';
        const valueElement = button.querySelector('.accessibility-widget__control-value');
        if (valueElement) valueElement.textContent = valueText;
        // Cycling controls carry the current value in the accessible name
        // instead of aria-pressed, which is misleading for >2 states.
        button.setAttribute('aria-label', `${label}: ${valueText}`);
        button.classList.toggle('is-active', String(value) !== String(CYCLES[key]?.[0]));
      } else {
        button.setAttribute('aria-pressed', String(Boolean(value)));
        button.classList.toggle('is-active', Boolean(value));
      }
    }

    const anyActive = Object.keys(DEFAULT_PREFS).some(
      (key) => key !== 'v' && this.#prefs[key] !== DEFAULT_PREFS[key]
    );
    this.refs.trigger.classList.toggle('is-active', anyActive);
  }

  /** @param {string} key */
  #announceChange(key) {
    const button = this.#controls().find((el) => el.getAttribute('data-key') === key);
    if (!button) return;
    const label = button.getAttribute('data-label') ?? '';
    const value = this.#prefs[key];
    const valueText = button.hasAttribute('data-cycle')
      ? button.getAttribute(`data-value-${value}`) ?? ''
      : value
        ? this.dataset.stateOn ?? ''
        : this.dataset.stateOff ?? '';
    this.#announce(`${label}: ${valueText}`);
  }

  /** @param {string} message */
  #announce(message) {
    const { announcer } = this.refs;
    if (!(announcer instanceof HTMLElement)) return;
    announcer.textContent = '';
    requestAnimationFrame(() => {
      announcer.textContent = message;
    });
  }

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key === 'Escape' && this.#isOpen) {
      this.closePanel();
      return;
    }

    // e.code is layout-independent — on macOS Alt+A produces 'å' for e.key.
    if (event.altKey && !event.ctrlKey && !event.metaKey && !event.shiftKey && event.code === 'KeyA') {
      event.preventDefault();
      this.togglePanel();
    }
  };

  /** @param {MouseEvent} event */
  #onDocumentClick = (event) => {
    if (!this.#isOpen) return;
    if (event.composedPath().includes(this)) return;
    this.closePanel({ restoreFocus: false });
  };
}

if (!customElements.get('accessibility-widget')) {
  customElements.define('accessibility-widget', AccessibilityWidget);
}
